import { Box, useTheme } from "@outschool/backpack";
import { Editor as TiptapEditor } from "@tiptap/react";
import React from "react";

import EditorContent from "./EditorContent";
import MenuBar from "./MenuBar";
import { useTiptapEditor } from "./TiptapEditor";

export type RichTextEditorHandle = {
  setHtml: (html: string) => void;
  getHtml: () => string | undefined;
};

export type RichTextEditorProps = {
  content?: string;
  placeholder?: string;
  onChange?: (html: string) => void;
  onBlur?: (html: string) => void;
  sx?: any;
};

export const RichTextEditor = React.forwardRef<
  RichTextEditorHandle,
  RichTextEditorProps
>(function RichTextEditor(
  {
    content = "",
    placeholder = "",
    onChange,
    onBlur,
    sx = {},
  }: RichTextEditorProps = {},
  ref,
) {
  const { tiptapEditor, setHtml, getHtml } = useTiptapEditor({
    content,
    placeholder,
  });
  React.useImperativeHandle(ref, () => ({ setHtml, getHtml }), [
    setHtml,
    getHtml,
  ]);

  return (
    <Component
      tiptapEditor={tiptapEditor}
      getHtml={getHtml}
      sx={sx}
      onChange={onChange}
      onBlur={onBlur}
    />
  );
});

function Component({
  tiptapEditor,
  getHtml,
  sx,
  onChange,
  onBlur,
}: {
  tiptapEditor: TiptapEditor | null;
  getHtml: () => string;
  sx: any;
  onChange?: (html: string) => void;
  onBlur?: (html: string) => void;
}) {
  const theme = useTheme();

  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const onBlurRef = React.useRef(onBlur);
  React.useEffect(() => {
    onBlurRef.current = onBlur;
  }, [onBlur]);

  React.useEffect(() => {
    if (tiptapEditor) {
      const onUpdate = () => {
        onChangeRef.current?.(getHtml());
      };
      tiptapEditor.on("update", onUpdate);
      return () => {
        tiptapEditor.off("update", onUpdate);
      };
    }
    return undefined;
  }, [tiptapEditor, getHtml]);

  React.useEffect(() => {
    if (tiptapEditor) {
      const handleBlur = () => {
        onBlurRef.current?.(getHtml());
      };
      tiptapEditor.on("blur", handleBlur);
      return () => {
        tiptapEditor.off("blur", handleBlur);
      };
    }
    return undefined;
  }, [tiptapEditor, getHtml]);

  if (!tiptapEditor) {
    return null;
  }
  return (
    <Box flex sx={{ ...sx, flexDirection: "column" }}>
      <MenuBar editor={tiptapEditor} />
      <EditorContent
        tiptapEditor={tiptapEditor}
        tiptapSx={{
          cursor: "text",
          overflow: "hidden",
          borderWidth: "1px",
          borderStyle: "solid",
          borderColor: theme.palette.neutral[400],
          backgroundColor: "white",
          "&:focus,&:focus-within,&:focus-visible": {
            outlineColor: theme.palette.primary.main,
            outlineWidth: "2px",
          },
          borderRadius: theme.spacing(8),
          minHeight: theme.spacing(88),
          paddingY: theme.spacing(10),
          paddingX: theme.spacing(14),
        }}
      />
    </Box>
  );
}
